import {useNavigate, useSearchParams} from "react-router-dom";
import {resolveTab} from "src/utils/tab.util";
import {useTranslation} from "react-i18next";
import {useEntity} from "crqlar-react-common/dist/components/useEntity";
import SettingsEntityHeader from "../../../layout/SettingsEntityHeader";
import DetailsLoading from "crqlar-react-common/dist/components/DetailsLoading";
import DangerZone from "crqlar-react-common/dist/components/DangerZone";
import {FiasService} from "crqlar-react-common/dist/services/integrations/fias/fias.service";
import {Box, Chip, Divider, Stack, Tab, Tabs, Typography} from "@mui/material";
import {useTrackScreenVisit} from "crqlar-react-common/dist/utils/analytics.util";
import FiasConfigForm from "./FiasConfigForm";
import {FiasConfig} from "crqlar-react-common/models/integrations/fias/fiasConfig";
import {useQuery} from "react-query";
import EmptyState from "crqlar-react-common/dist/components/EmptyState";
import Button from "@mui/material/Button";
import ElectricalServicesIcon from "@mui/icons-material/ElectricalServices";


const FiasConfigDetails = () => {

    useTrackScreenVisit('system fias config details')

    //region State

    const {t} = useTranslation()
    const navigate = useNavigate()

    const [searchParams, setSearchParams] = useSearchParams();
    const activeTab = resolveTab(searchParams.get('tab'), ['config', 'logs'], 'config');
    const setActiveTab = (newTab: string) => {
        setSearchParams({tab: newTab});
    };

    const fiasConfigData = useEntity<FiasConfig>({
        get: async () => {
            return await FiasService.find()
        },
        create: FiasService.create,
        update: FiasService.update,
        delete: (id) => FiasService.delete({id: [id]}),
        isValid: (fiasConfig) => {
            return !!fiasConfig.name
        },
        cleanupBeforeSave: (fiasConfig) => {
            return {
                ...fiasConfig,
                name: fiasConfig.name?.trim()
            }
        }
    })

    const fiasConfig = fiasConfigData.item

    const handleCreate = () => fiasConfigData.startCreate()

    const {data: statsData} = useQuery({
        queryKey: ['fias-stats', fiasConfig.id],
        queryFn: () => FiasService.find(),
        enabled: !!fiasConfig.id,
        refetchInterval: 1000
    })

    //endregion State

    const headerSection = (
        <SettingsEntityHeader
            title={fiasConfig.name}
            data={fiasConfigData}
            back={() => navigate('/system')}
        />
    )

    const dangerZoneSection = (
        <Stack gap={{xs: 2.5, sm: 2}}>
            <DangerZone
                title={t('Delete integration')}
                description={t('This permanently deletes the integration configuration and stops its syncing immediately. This action cannot be undone.')}
                buttonLabel={t('Delete')}
                loading={fiasConfigData.saving}
                onDelete={() => fiasConfigData.removeNow()}
                reloadOnDelete
            />

            <Typography variant={'body2'} color={'text.secondary'} textAlign={'start'}>
                {t('Last updated')}: {fiasConfig.updateDate?.toLocaleString() || '-'}
            </Typography>
        </Stack>
    )

    const content = (
        <>
            {headerSection}

            <Stack
                sx={{
                    width: '100%',
                    maxWidth: '900px',
                    mx: 'auto',
                    mt: 3,
                    gap: 6,
                }}
            >
                {
                    fiasConfigData.isMissing &&
                    <EmptyState
                        icon={<ElectricalServicesIcon/>}
                        title={t('No integration configured')}
                        description={t('Create the configuration to connect this integration.')}
                    >
                        <Button variant={'contained'} onClick={handleCreate}>
                            {t('Create')}
                        </Button>
                    </EmptyState>
                }

                {
                    !!fiasConfig.id &&
                    <Tabs
                        value={activeTab}
                        onChange={(e, v) => setActiveTab(v)}
                        aria-label="tabs"
                        sx={{
                            borderBottom: 1,
                            borderColor: 'divider',
                        }}
                    >
                        <Tab label={t('Configuration')} value={'config'}/>
                        <Tab label={t('Status and Logs')} value={'logs'}/>
                    </Tabs>
                }

                {
                    !fiasConfigData.isMissing && activeTab === 'config' && (
                        <>
                            <FiasConfigForm fiasConfigData={fiasConfigData}/>

                            {
                                !!fiasConfig.id &&
                                <>
                                    <Divider sx={{mt: 4}}/>
                                    {dangerZoneSection}
                                </>
                            }
                        </>
                    )
                }

                {
                    activeTab === 'logs' && (
                        <Box>
                            <Typography variant="h6" sx={{mb: 2}}>
                                {t('Stats')}
                            </Typography>
                            {statsData?.stats && (
                                <Box sx={{mb: 3}}>
                                    <Box sx={{display: 'flex', gap: 2, mb: 2}}>
                                        <Chip
                                            label={`Status: ${statsData.stats.connectionStatus}`}
                                            color={
                                                statsData.stats.connectionStatus === 'CONNECTED' ? 'success' :
                                                    statsData.stats.connectionStatus === 'CONNECTING' ? 'warning' : 'default'
                                            }
                                        />
                                    </Box>
                                    {statsData.stats.statsDate && (
                                        <Typography variant="body2" sx={{mb: 1}}>
                                            {t('Stats Date')}: {new Date(statsData.stats.statsDate).toLocaleString()}
                                        </Typography>
                                    )}
                                    {statsData.stats.lastLaReceivedAt && (
                                        <Typography variant="body2" sx={{mb: 1}}>
                                            {t('Last LA Received')}: {new Date(statsData.stats.lastLaReceivedAt).toLocaleString()}
                                        </Typography>
                                    )}
                                    {statsData.stats.latestLogs && statsData.stats.latestLogs.length > 0 && (
                                        <Box sx={{mt: 2}}>
                                            <Typography variant="h6" sx={{mb: 1}}>
                                                {t('Latest Logs')}
                                            </Typography>
                                            {statsData.stats.latestLogs.map((log, index) => (
                                                <Typography key={index} variant="body2" sx={{fontFamily: 'monospace', mb: 0.5}}>
                                                    {log}
                                                </Typography>
                                            ))}
                                        </Box>
                                    )}
                                </Box>
                            )}
                            {!statsData?.stats && (
                                <Typography variant="body2" color="text.secondary">
                                    {t('No stats available')}
                                </Typography>
                            )}
                        </Box>
                    )
                }
            </Stack>
        </>
    )

    return (
        <Box sx={{width: '100%', pb: 8}}>
            {fiasConfigData.loading ? <>{headerSection}<DetailsLoading/></> : content}
        </Box>
    );
}

export default FiasConfigDetails;
